/**
 * HML runs the same Laravel API on several servers, each with its own
 * transaction database:
 *   - MTN server                (REACT_APP_MTN_BASE_API_URL, .92)
 *   - Airtel & 9mobile API      (REACT_APP_AIRTEL_9MOBILE_API_URL, .243):
 *     sends Airtel subscription requests, runs UAT tests
 *   - Airtel & 9mobile reports  (REACT_APP_AIRTEL_REPORT_URL, .94): receives
 *     the Airtel PPO callbacks and holds the Airtel subscriptions. Falls back
 *     to REACT_APP_AIRTEL_9MOBILE_API_URL when unset.
 *
 * Users, products and login tokens are shared (logins happen on the MTN
 * server and its tokens are copied to the others), so the same session works
 * everywhere. Routing, in one place (see baseUrlFor):
 *   - Airtel-server-only paths always go to the Airtel & 9mobile API server,
 *     whatever the switch says (AIRTEL_SERVER_ONLY_PREFIXES).
 *   - Reports read the selected server (SERVER_SCOPED_PREFIXES); for
 *     "Airtel & 9mobile" that is its report URL.
 *   - Everything else (product and account management) goes to the MTN
 *     server, the only one that accepts those writes.
 */

const STORAGE_KEY = 'hml.server';

export const SERVERS = [
  {
    key: 'mtn',
    label: 'MTN',
    longLabel: 'MTN server',
    baseUrl: process.env.REACT_APP_MTN_BASE_API_URL,
    reportBaseUrl: process.env.REACT_APP_MTN_BASE_API_URL,
    telcos: ['MTN'],
  },
  {
    key: 'airtel',
    label: 'Airtel & 9mobile',
    longLabel: 'Airtel & 9mobile server',
    baseUrl: process.env.REACT_APP_AIRTEL_9MOBILE_API_URL,
    // Report reads go where the Airtel PPO data lives (.94).
    reportBaseUrl:
      process.env.REACT_APP_AIRTEL_REPORT_URL || process.env.REACT_APP_AIRTEL_9MOBILE_API_URL,
    telcos: ['AIRTEL', 'NINEMOBILE'],
  },
].filter((s) => !!s.baseUrl);

export const PRIMARY_BASE_URL = process.env.REACT_APP_MTN_BASE_API_URL;

/** Paths that read a server's own transaction data and so follow the switch. */
const SERVER_SCOPED_PREFIXES = [
  '/admin/report/',
  '/admin/report-daily',
  '/admin/log-tracer/',
  '/partner/report/',
  '/partner/dashboard',
  '/partner/sync-failures/',
  '/partner/events/',
];

/**
 * Paths that exist only where the Airtel traffic runs, so they always go to
 * the Airtel & 9mobile server regardless of the switch. UAT test runs: the
 * Airtel UAT request, the PPO callback and the welcome SMS all happen there,
 * and the runs are stored in that server's own database.
 */
const AIRTEL_SERVER_ONLY_PREFIXES = ['/admin/uat-test-runs'];

export const AIRTEL_BASE_URL = process.env.REACT_APP_AIRTEL_9MOBILE_API_URL;

let current = readStored();
const listeners = new Set();

function readStored() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (SERVERS.some((s) => s.key === stored)) return stored;
  } catch (e) {
    /* storage unavailable */
  }
  return SERVERS[0] ? SERVERS[0].key : 'mtn';
}

export function getServerKey() {
  return current;
}

export function getServer(key = current) {
  return SERVERS.find((s) => s.key === key) || SERVERS[0];
}

export function setServerKey(key) {
  if (!SERVERS.some((s) => s.key === key) || key === current) return;
  current = key;
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
  } catch (e) {
    /* ignore */
  }
  listeners.forEach((fn) => fn(key));
}

export function subscribeServer(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isServerScopedPath(path) {
  return SERVER_SCOPED_PREFIXES.some((p) => path.startsWith(p));
}

export function isAirtelServerOnlyPath(path) {
  return AIRTEL_SERVER_ONLY_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)
  );
}

/** The Airtel & 9mobile server entry (for showing its name). */
export function airtelServer() {
  return SERVERS.find((s) => s.key === 'airtel') || null;
}

/**
 * Base URL for an API path: Airtel-only paths always use the Airtel &
 * 9mobile API server, report reads follow the switch (to that server's
 * report URL), everything else is primary.
 */
export function baseUrlFor(path) {
  if (isAirtelServerOnlyPath(path)) {
    if (!AIRTEL_BASE_URL) {
      // eslint-disable-next-line no-console
      console.error('REACT_APP_AIRTEL_9MOBILE_API_URL is not set; Airtel-only calls cannot be routed.');
    }
    return AIRTEL_BASE_URL || PRIMARY_BASE_URL;
  }
  if (isServerScopedPath(path)) {
    const server = getServer();
    if (server && (server.reportBaseUrl || server.baseUrl)) return server.reportBaseUrl || server.baseUrl;
  }
  return PRIMARY_BASE_URL;
}

/**
 * True when a request URL goes to the MTN (login) server. Only that server's
 * 401 means the session is over: the other servers receive login tokens a
 * moment after sign-in (copied from the MTN server), so a 401 there can just
 * mean "not copied yet" and must not sign the user out.
 */
export function isPrimaryUrl(url) {
  return !!PRIMARY_BASE_URL && typeof url === 'string' && url.startsWith(PRIMARY_BASE_URL);
}

/**
 * Rewrites an absolute URL built against the MTN base (as the older pages
 * do) so report reads go to the currently selected server and Airtel-only
 * paths go to the Airtel & 9mobile server.
 */
export function rewriteLegacyUrl(url) {
  if (!PRIMARY_BASE_URL || typeof url !== 'string' || !url.startsWith(PRIMARY_BASE_URL)) {
    return url;
  }
  const path = url.slice(PRIMARY_BASE_URL.length);
  if (!isServerScopedPath(path) && !isAirtelServerOnlyPath(path)) return url;
  return `${baseUrlFor(path)}${path}`;
}
